import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Bot, Check, FileText, Loader2, Mic } from 'lucide-react';
import type { ConsentKind, LegalDocumentDTO } from '../../global';
import { BrandMark } from '../../components/ui/BrandMark';
import { useLanguage } from '../../lib/i18n';
import { IS_NATIVE } from '../../lib/platform';

type Step = 'legal' | 'ready';

interface OnboardingViewProps {
  pending: LegalDocumentDTO[];
  needsSetup: boolean;
  firstName: string | null;
  onAccept: (documents: LegalDocumentDTO[]) => Promise<void>;
  onComplete: () => Promise<void>;
  onSignOut: () => void;
}

/**
 * Girişten sonraki ilk ekran. Yasal onay adımı yalnızca güncel sürümü
 * onaylanmamış metin varsa; "Hazırsın" adımı yalnızca bu cihazdaki ilk
 * açılışta. Metinler değişince kullanıcı yalnızca onay adımını görür.
 * Telefonda her adım tek ekrana sığar (aşağı kaydırma yok), düğmeler altta.
 */
export const OnboardingView: React.FC<OnboardingViewProps> = ({
  pending,
  needsSetup,
  firstName,
  onAccept,
  onComplete,
  onSignOut,
}) => {
  const { t, language, setLanguage } = useLanguage();
  const steps = useMemo<Step[]>(
    () => [...(pending.length > 0 ? (['legal'] as const) : []), ...(needsSetup ? (['ready'] as const) : [])],
    [pending.length, needsSetup]
  );
  const [step, setStep] = useState<Step>(steps[0] ?? 'ready');
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Onaydan sonra `pending` boşalınca adım listesi kısalır; geçerli adım
  // listeden düştüyse bir sonrakine geç.
  useEffect(() => {
    if (!steps.includes(step) && steps[0]) setStep(steps[0]);
  }, [steps, step]);

  const run = async (action: () => Promise<void>) => {
    setIsBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError((err as Error).message || t('Bir sorun oluştu, tekrar dene.', 'Something went wrong, try again.'));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="h-full w-full overflow-y-auto bg-[#111113] text-[#F0EEE6] select-none">
      <div className="min-h-full flex flex-col px-5 pt-2 pb-5 md:items-center md:justify-center md:px-6 md:py-10">
        <div className="w-full max-w-lg mx-auto flex-1 md:flex-none flex flex-col gap-5">
          <div className="flex items-center justify-between h-11">
            <div className="flex items-center gap-1.5" aria-label={t('Adımlar', 'Steps')}>
              {steps.length > 1 &&
                steps.map((item) => (
                  <span
                    key={item}
                    className={`h-1.5 rounded-full transition-all ${
                      item === step ? 'w-6 bg-[#46CBE2]' : 'w-1.5 bg-neutral-600'
                    }`}
                  />
                ))}
            </div>
            <button
              onClick={() => void setLanguage(language === 'tr' ? 'en' : 'tr')}
              className="h-11 px-1 text-[14px] font-semibold text-neutral-400 hover:text-neutral-200 transition-colors"
            >
              {language === 'tr' ? 'English' : 'Türkçe'}
            </button>
          </div>

          {step === 'legal' && (
            <LegalStep
              documents={pending}
              isUpdate={!needsSetup}
              firstName={firstName}
              isBusy={isBusy}
              onAccept={() => run(() => onAccept(pending))}
              onSignOut={onSignOut}
            />
          )}
          {step === 'ready' && (
            <ReadyStep firstName={firstName} isBusy={isBusy} onFinish={() => void run(onComplete)} />
          )}

          {error && (
            <div className="text-[13px] text-rose-400 flex items-start gap-2 bg-rose-500/10 border border-rose-500/25 rounded-xl p-3">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span className="leading-relaxed">{error}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// — Adımlar —

const primaryButton =
  'w-full h-[54px] flex items-center justify-center gap-2 rounded-2xl bg-[#46CBE2] text-[#062A31] font-bold text-[16px] hover:bg-[#5ED3E7] disabled:bg-[#232327] disabled:text-[#6B6B73] disabled:cursor-not-allowed transition-colors active:scale-[0.99]';

/** Satırın sağındaki bağlantı/düğme: en az 44 px dokunma alanı. */
const sideAction =
  'shrink-0 min-w-11 h-11 -mt-2.5 -mr-1 px-1 flex items-center justify-center text-[14px] font-semibold text-[#46CBE2] hover:text-[#7BDAEA] transition-colors';

/** Onay maddesinin kısa adı ve tek cümlelik açıklaması (taslak A, 2026-10-20). */
function consentCopy(doc: LegalDocumentDTO, t: (tr: string, en: string) => string, language: string) {
  switch (doc.kind as ConsentKind) {
    case 'terms':
      return {
        title: t('Kullanım Koşulları', 'Terms of Use'),
        text: t('Veri İşleme Eki dahil. Okudum, kabul ediyorum.', 'Including the Data Processing Addendum. I have read and accept them.'),
      };
    case 'privacy':
      return {
        title: t('Aydınlatma Metni', 'Privacy Notice'),
        // Kurul 2026/347: aydınlatma sözleşme değildir; "kabul ediyorum" değil "okudum ve anladım".
        text: t('Verilerimin nasıl işlendiğini okudum ve anladım.', 'I have read and understood how my data is processed.'),
      };
    case 'recording':
      return {
        title: t('Kayıt sorumluluğu', 'Recording responsibility'),
        text: t(
          'Kayıttan önce katılımcıları bilgilendiririm. Habersiz kayıt suç olabilir (TCK 133).',
          'I tell participants before I record. Recording people without their knowledge can be a crime.'
        ),
      };
    case 'transfer':
      // Aydınlatmadan ve koşullardan ayrı, ayrı metin (Kurul 2026/347). Verilmezse bulut işleme çalışmaz.
      // Cümle, rıza sayfasındaki (yurt-disi-aktarim-acik-riza) veri türleri ve hizmetlerle aynı kalmalı.
      return {
        title: t('Yurt dışındaki hizmet sağlayıcılar', 'Service providers abroad'),
        text: t(
          'Sesim, konuşma metnim, özetim, toplantı bilgilerim ve sohbet sorularım yurt dışındaki hizmet sağlayıcılarda (yapay zekâ, döküm, toplantı botu, e-posta) işlenir. Buna açık rıza veriyorum.',
          'My voice, transcripts, summaries, meeting details and chat questions are processed by service providers abroad (AI, transcription, meeting bot, email). I give my explicit consent to this.'
        ),
      };
    default:
      return { title: language === 'en' ? doc.title.en : doc.title.tr, text: t('Okudum, kabul ediyorum.', 'I have read and accept it.') };
  }
}

const COUNT_TR = ['', 'bir', 'iki', 'üç', 'dört'];

const LegalStep: React.FC<{
  documents: LegalDocumentDTO[];
  isUpdate: boolean;
  firstName: string | null;
  isBusy: boolean;
  onAccept: () => void;
  onSignOut: () => void;
}> = ({ documents, isUpdate, firstName, isBusy, onAccept, onSignOut }) => {
  const { t, language } = useLanguage();
  const [checked, setChecked] = useState<Partial<Record<ConsentKind, boolean>>>({});
  const [whyOpen, setWhyOpen] = useState(false);
  const allChecked = documents.every((doc) => checked[doc.kind]);
  const open = (url: string) => void window.zabit.openExternal(url).catch(() => {});
  const count = documents.length;

  return (
    <>
      <div className="space-y-2">
        <h1 className="text-[28px] leading-[34px] font-bold tracking-tight">
          {isUpdate
            ? t('Metinlerimizi güncelledik', 'We’ve updated our terms')
            : firstName
              ? t(`Hoş geldin, ${firstName}`, `Welcome, ${firstName}`)
              : t('TalkNote’a hoş geldin', 'Welcome to TalkNote')}
        </h1>
        <p className="text-[15px] leading-[22px] text-neutral-400">
          {isUpdate
            ? t('Devam etmek için güncel metinleri onayla.', 'Accept the updated documents to continue.')
            : t(
                `Başlamadan önce ${COUNT_TR[count] ?? count} şeyi onayla.`,
                count === 1 ? 'One thing before you start.' : `${count} things before you start.`
              )}
        </p>
      </div>

      <div className="rounded-[20px] border border-[#2E2E34] bg-[#19191C] divide-y divide-[#2E2E34]">
        {documents.map((doc) => {
          const copy = consentCopy(doc, t, language);
          const isChecked = Boolean(checked[doc.kind]);
          return (
            <div key={doc.kind} className="flex items-start gap-2 pl-4 pr-3 py-4">
              <label className="flex-1 min-w-0 flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(event) => setChecked((prev) => ({ ...prev, [doc.kind]: event.target.checked }))}
                  className="sr-only peer"
                />
                <span
                  aria-hidden="true"
                  className={`mt-px w-6 h-6 shrink-0 rounded-[7px] border-[1.5px] flex items-center justify-center transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-[#46CBE2] ${
                    isChecked ? 'bg-[#46CBE2] border-[#46CBE2] text-[#062A31]' : 'border-neutral-500'
                  }`}
                >
                  {isChecked && <Check className="w-4 h-4" strokeWidth={3} />}
                </span>
                <span className="min-w-0 flex flex-col gap-0.5">
                  <span className="text-[15.5px] leading-[21px] font-semibold">{copy.title}</span>
                  <span className="text-[13.5px] leading-[19px] text-neutral-400">{copy.text}</span>
                </span>
              </label>
              {doc.kind === 'recording' ? (
                <button onClick={() => setWhyOpen(true)} className={sideAction}>
                  {t('Neden?', 'Why?')}
                </button>
              ) : (
                <button onClick={() => open(doc.url)} className={sideAction}>
                  {t('Oku', 'Read')}
                </button>
              )}
            </div>
          );
        })}
      </div>
      {documents.some((doc) => doc.kind === 'terms') && (
        // Veri İşleme Eki koşulların parçası; ayrı sürümü yok, aynı onayla kabul ediliyor.
        <button
          onClick={() => {
            const terms = documents.find((doc) => doc.kind === 'terms');
            if (terms) open(new URL('/veri-isleme', terms.url).href);
          }}
          className="-mt-2 self-start h-9 text-[13px] font-semibold text-[#46CBE2] hover:text-[#7BDAEA] transition-colors"
        >
          {t('Veri İşleme Eki’ni oku', 'Read the Data Processing Addendum')}
        </button>
      )}

      <div className="mt-auto md:mt-2 space-y-1.5">
        <button onClick={onAccept} disabled={!allChecked || isBusy} className={primaryButton}>
          {isBusy && <Loader2 className="w-4 h-4 animate-spin" />}
          {t('Kabul et ve devam et', 'Accept and continue')}
        </button>
        <button
          onClick={onSignOut}
          className="w-full h-11 text-[14px] text-neutral-500 hover:text-neutral-300 transition-colors"
        >
          {t('Kabul etmiyorum, çıkış yap', 'I don’t accept — sign out')}
        </button>
      </div>

      {whyOpen && <WhySheet onClose={() => setWhyOpen(false)} recordingUrl={documents.find((d) => d.kind === 'recording')?.url} />}
    </>
  );
};

/** "Neden?": kayıt sorumluluğunun ayrıntısı, alttan açılan sayfa. Geri tuşu (Escape) kapatır. */
const WhySheet: React.FC<{ onClose: () => void; recordingUrl?: string }> = ({ onClose, recordingUrl }) => {
  const { t } = useLanguage();
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center md:justify-center" role="dialog" aria-modal="true" aria-label={t('Neden önemli?', 'Why it matters')}>
      <button type="button" aria-label={t('Kapat', 'Close')} onClick={onClose} className="absolute inset-0 w-full bg-black/55" />
      <div className="relative w-full md:max-w-md rounded-t-[26px] md:rounded-[22px] bg-[#19191C] px-6 pt-2.5 pb-7 space-y-3.5 animate-sheet-up md:animate-none">
        <span className="block mx-auto w-10 h-[5px] rounded-full bg-[#3A3A41] md:hidden" />
        <h2 className="text-[20px] leading-[26px] font-bold">{t('Neden önemli?', 'Why it matters')}</h2>
        <ul className="list-disc pl-[18px] space-y-2.5 text-[14.5px] leading-[21px] text-neutral-300">
          <li>
            {t(
              'Kaydettiğin toplantılardaki kişisel verilerden sen sorumlusun; TalkNote onları senin adına işler.',
              'You are responsible for the personal data in the meetings you record; TalkNote processes it on your behalf.'
            )}
          </li>
          <li>
            {t(
              'Katılımcıların haberi olmadan kayıt yapmak suç olabilir (TCK 133).',
              'Recording a private conversation without the participants knowing can be a crime (Turkish Penal Code art. 133).'
            )}
          </li>
          <li>
            {t(
              'Bot girince sohbete kayıt yaptığını yazar. Bu, senin bilgilendirmenin yerine geçmez.',
              'The bot posts a recording notice in the chat when it joins. That doesn’t replace telling people yourself.'
            )}
          </li>
        </ul>
        {recordingUrl && (
          <button
            onClick={() => void window.zabit.openExternal(recordingUrl).catch(() => {})}
            className="h-10 text-[14px] font-semibold text-[#46CBE2] hover:text-[#7BDAEA] transition-colors"
          >
            {t('Koşullardaki ilgili bölüm', 'Read the full section')}
          </button>
        )}
        <button onClick={onClose} className="w-full h-[52px] rounded-2xl bg-[#232327] text-[15.5px] font-semibold hover:bg-[#2A2A2F] transition-colors">
          {t('Anladım', 'Got it')}
        </button>
      </div>
    </div>
  );
};

/** Hazırsın: hoş geldin animasyonu (logo gelir, halka çizilir, yazılar sırayla belirir). */
const ReadyStep: React.FC<{ firstName: string | null; isBusy: boolean; onFinish: () => void }> = ({
  firstName,
  isBusy,
  onFinish,
}) => {
  const { t } = useLanguage();
  const tips = [
    {
      icon: Mic,
      title: t('Kayıt al', 'Record'),
      text: IS_NATIVE
        ? t('Yüz yüze toplantıları telefonla kaydet.', 'Record in-person meetings with your phone.')
        : t('Görüşmelerini tek tıkla kaydet.', 'Record your calls with one click.'),
    },
    {
      icon: Bot,
      title: t('Bot katılır', 'The bot joins'),
      text: t('Takvimindeki toplantılara bot kendiliğinden katılır.', 'The bot joins the meetings on your calendar by itself.'),
    },
    {
      icon: FileText,
      title: t('Özet hazır', 'Summary ready'),
      text: t('Transkript ve özet kendiliğinden gelir.', 'The transcript and summary arrive on their own.'),
    },
  ];

  return (
    <>
      <div className="flex-1 md:flex-none flex flex-col items-center justify-center text-center gap-5 py-4">
        <div className="relative w-28 h-28 flex items-center justify-center">
          <span aria-hidden="true" className="welcome-ripple absolute inset-0 rounded-full border-2 border-[#519cf9]/60" />
          <span aria-hidden="true" className="welcome-ripple absolute inset-0 rounded-full border-2 border-[#519cf9]/40 [animation-delay:700ms]" />
          <BrandMark animated className="relative w-24 h-24 drop-shadow-[0_10px_30px_rgba(81,156,249,0.35)]" />
        </div>
        <div className="space-y-2">
          <h1 className="welcome-up text-[30px] leading-9 font-bold tracking-tight [animation-delay:650ms]">
            {firstName ? t(`Hoş geldin, ${firstName}!`, `Welcome, ${firstName}!`) : t('Hoş geldin!', 'Welcome!')}
          </h1>
          <p className="welcome-up text-[15px] leading-[22px] text-neutral-400 [animation-delay:800ms]">
            {t('Her şey hazır. TalkNote seninle.', 'You’re all set.')}
          </p>
        </div>
        <div className="w-full space-y-2.5 text-left">
          {tips.map(({ icon: Icon, title, text }, index) => (
            <div
              key={title}
              className="welcome-up flex items-center gap-3.5 rounded-2xl border border-[#2E2E34] bg-[#19191C] px-4 py-3.5"
              style={{ animationDelay: `${1000 + index * 140}ms` }}
            >
              <span className="w-10 h-10 shrink-0 rounded-xl bg-[#46CBE2]/[0.13] text-[#46CBE2] flex items-center justify-center">
                <Icon className="w-5 h-5" />
              </span>
              <span className="min-w-0">
                <span className="block text-[15px] leading-[21px] font-semibold">{title}</span>
                <span className="block text-[13.5px] leading-[19px] text-neutral-400">{text}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="welcome-up mt-auto md:mt-2 [animation-delay:1500ms]">
        <button onClick={onFinish} disabled={isBusy} className={primaryButton}>
          {isBusy && <Loader2 className="w-4 h-4 animate-spin" />}
          {t('TalkNote’u kullanmaya başla', 'Start using TalkNote')}
        </button>
      </div>
    </>
  );
};
